// The in-game phone. Three states: open, minimized (a pill that remembers your app), or put away.
// Switch apps from the home grid or the dock; minimize with ⌄, a swipe down on the top bar,
// Esc, or (on phones) by tapping the world behind it.
import { AdBanner } from "../ads/AdSlots";
import { useEffect, useRef, useState } from "preact/hooks";
import type { ComponentType } from "preact";
import { activeApp, appParams, gameState, hud, minimizePhone, openApp, phoneMin, phoneOpen } from "../bridge";
import { phoneModel } from "@degen/sim/phones/phones";
import { displayClock } from "../prefs";
import { APPS } from "./apps/registry";

const DOCK = ["pumpr", "chirp", "wallet", "messages", "map"];

function useApp(id: string | null) {
  const [Comp, setComp] = useState<ComponentType | null>(null);
  useEffect(() => {
    if (!id) return;
    let alive = true;
    setComp(null);
    APPS.find((a) => a.id === id)!
      .load()
      .then((m) => alive && setComp(() => m.default));
    return () => {
      alive = false;
    };
  }, [id]);
  return Comp;
}

/** Top status bar: Andro (punch-hole camera) or iFone (Dynamic Island). */
function StatusBar({ os }: { os: "andro" | "ifone" }) {
  const h = hud.value;
  return (
    <div class={`status-bar ${os}`} aria-hidden="true">
      <span class="sb-time">{h ? displayClock(h.clock) : ""}</span>
      {os === "ifone" ? <span class="sb-island" /> : <span class="sb-cam" />}
      <span class="sb-icons">
        <span class="sb-signal">▂▄▆</span> {os === "andro" ? "◢ 5G" : "5G"} <span class="sb-batt" />
      </span>
    </div>
  );
}

function Badge({ id }: { id: string }) {
  const n = id === "messages" ? hud.value?.unread ?? 0 : 0;
  return n > 0 ? <span class="app-badge">{n}</span> : null;
}

export function Phone() {
  const open = phoneOpen.value;
  const app = activeApp.value;
  const def = app ? APPS.find((a) => a.id === app) : null;
  const Comp = useApp(app);
  const drag = useRef<{ y: number; id: number; pull: number } | null>(null);
  const [pull, setPull] = useState(0);

  const model = phoneModel(hud.value?.phone ?? gameState()?.player.phone ?? "andro_a15");
  const os = model.os;
  const back = () => {
    // Back pops an in-app screen first (e.g. token detail), then goes to the home screen.
    if (Object.keys(appParams.value).length) appParams.value = {};
    else activeApp.value = null;
  };

  return (
    <>
      {/* Phones: tapping the world behind the phone puts it away (minimized). */}
      {open && <div class="phone-scrim" onClick={minimizePhone} aria-hidden="true" />}
      <aside
        class={`phone skin-${os} ${open ? "open" : ""} ${hud.value?.equipped.phoneSkin ? `wall-${hud.value.equipped.phoneSkin.slice(5)}` : ""}`}
        aria-hidden={!open}
        data-model={model.id}
        style={{ "--frame": model.color, ...(pull > 0 ? { transform: `translateY(${pull}px)`, transition: "none" } : {}) }}
      >
        <StatusBar os={os} />
        <div
          class="phone-top"
          onPointerDown={(e) => {
            if ((e.target as HTMLElement).closest("button")) return;
            drag.current = { y: e.clientY, id: e.pointerId, pull: 0 };
            try {
              e.currentTarget.setPointerCapture(e.pointerId);
            } catch {
              /* not supported */
            }
          }}
          onPointerMove={(e) => {
            if (drag.current?.id !== e.pointerId) return;
            drag.current.pull = Math.max(0, e.clientY - drag.current.y);
            setPull(drag.current.pull);
          }}
          onPointerUp={() => {
            if ((drag.current?.pull ?? 0) > 70) minimizePhone();
            drag.current = null;
            setPull(0);
          }}
          onPointerCancel={() => {
            drag.current = null;
            setPull(0);
          }}
        >
          <div class="phone-handle" aria-hidden="true" />
          <div class="phone-bar">
            {app ? (
              <button class="pbtn back" onClick={back} aria-label="Back">
                {os === "ifone" ? "‹ Back" : "←"}
              </button>
            ) : (
              <span class="pbtn-spacer" />
            )}
            <span class="phone-title">{def ? `${def.icon} ${def.name}` : model.name}</span>
            {app && (
              <button class="pbtn" onClick={() => (activeApp.value = null)} aria-label="Home screen">
                ⌂
              </button>
            )}
            <button class="pbtn" onClick={minimizePhone} aria-label="Minimize phone">
              ⌄
            </button>
          </div>
        </div>
        <div class="phone-body">
          {app ? (
            <div class="app-body">{Comp ? <Comp /> : <div class="muted pad">Loading…</div>}</div>
          ) : (
            <>
            <AdBanner slot="phone.home.banner" dismissible />
            <div class="home-grid">
              {APPS.map((a) => (
                <button key={a.id} class="app-icon" onClick={() => openApp(a.id)}>
                  <span class="icon" style={{ background: a.color }}>
                    {a.icon}
                    <Badge id={a.id} />
                  </span>
                  <span class="app-name">{a.name}</span>
                </button>
              ))}
            </div>
            </>
          )}
        </div>
        <nav class="phone-dock" aria-label="Quick switch">
          {DOCK.map((id) => {
            const a = APPS.find((x) => x.id === id)!;
            return (
              <button key={id} class={`dock-app ${app === id ? "on" : ""}`} onClick={() => openApp(id)} aria-label={a.name}>
                <span class="icon" style={{ background: a.color }}>
                  {a.icon}
                  <Badge id={id} />
                </span>
              </button>
            );
          })}
        </nav>
        {os === "andro" ? (
          <nav class="andro-nav" aria-label="Navigation">
            <button onClick={back} aria-label="Back" disabled={!app}>
              ◁
            </button>
            <button onClick={() => (activeApp.value = null)} aria-label="Home screen">
              ○
            </button>
            <button onClick={minimizePhone} aria-label="Minimize phone">
              ▢
            </button>
          </nav>
        ) : (
          <button class="ios-home" onClick={() => (activeApp.value = null)} aria-label="Home screen">
            <span />
          </button>
        )}
      </aside>
      {!open && phoneMin.value && def && (
        <button class="phone-pill" onClick={() => (phoneOpen.value = true)} aria-label={`Back to ${def.name}`}>
          <span>{def.icon}</span>
          <span>{def.name}</span>
          <span class="muted">▲</span>
        </button>
      )}
    </>
  );
}
